import { memo, useCallback, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Spinner } from "react-bootstrap";
import { useGetUser } from "../../api/queries/useGetUser";
import {
  isPaidPortalMember,
  PROFILE_MEMBERSHIP_FIELD_KEYS,
} from "../../utils/membershipTier";

const BTN_LINK_STYLE = { textDecoration: "none" as const };
const IMG_SIZE = 100;
const CARD_BODY_MIN_HEIGHT = 320;

const EXCLUDED_KEYS = [
  "password",
  "objectId",
  "isGDPRConsent",
  "isEmailUpdates",
  "isTermConditions",
  "isDirectory",
  "hasPaid",
  "hasCompletedProfile",
  "id",
  "isAdmin",
  "membershipId",
  "consentToShareProfile",
  "BannerImage",
];

function getImageAlt(key: string): string {
  if (key === "IdDocument") return "ID document";
  if (key === "BusinessLogo") return "Business logo";
  if (key === "PersonalProfessionalPhoto" || key === "ProfessionalPhoto") return "Professional photo";
  return "Profile image";
}

function UserProfileInner() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: objectId } = useParams();
  const { data, isLoading } = useGetUser({ id: objectId || "", enabled: !!objectId });
  const userData = { ...data?.user, ...data?.imageUrls };
  const showMembershipFields = useMemo(
    () => isPaidPortalMember(userData),
    [userData]
  );

  const handleBack = useCallback(() => {
    const state = (location.state as {
      from?: string;
      adminTab?: string;
      usersSubTab?: "all" | "members" | "explorer" | "nonusers";
    } | null);
    if (state?.from) {
      navigate(state.from, {
        state: {
          adminTab: state.adminTab,
          usersSubTab: state.usersSubTab,
        },
      });
      return;
    }
    navigate(-1);
  }, [location.state, navigate]);

  return (
    <div className="container p-4 pt-5 pt-md-5">
      <div className="card shadow-lg border-0">
        <div className="card-header gradient-bg text-white d-flex align-items-center">
          <button
            className="btn btn-link text-white me-2 d-inline-flex align-items-center gap-2"
            onClick={handleBack}
            style={BTN_LINK_STYLE}
            type="button"
            aria-label="Go back"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            <span>Back</span>
          </button>
          <h4 className="mb-0">User Profile</h4>
        </div>
        <div className="card-body" style={{ minHeight: CARD_BODY_MIN_HEIGHT }}>
          {isLoading ? (
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" variant="primary" role="status" />
            </div>
          ) : (
            Object.entries(userData || {})
              .filter(
                ([key, value]) =>
                  key.trim() !== "" &&
                  value !== null &&
                  value !== "" &&
                  !EXCLUDED_KEYS.includes(key) &&
                  (showMembershipFields || !PROFILE_MEMBERSHIP_FIELD_KEYS.has(key))
              )
              .map(([key, value]: [string, unknown]) => (
                <div className="mb-1 row" key={key}>
                  <label className="col-sm-4 col-form-label fw-bold text-capitalize">
                    {key === "isAnnualMembership"
                      ? "Membership Type"
                      : key === "businessAddressLineOne"
                      ? "Address Line 1"
                      : key === "businessAddressLineTwo"
                      ? "Address Line 2"
                      : key === "businessName"
                      ? "Business Name"
                      : key
                          .replace(/^(business|personal)/i, "")
                          .replace(/([A-Z])/g, " $1")
                          .trim()}
                    :
                  </label>
                  <div className="col-sm-8">
                    {key === "IdDocument" ||
                    key === "BusinessLogo" ||
                    key === "PersonalProfessionalPhoto" ||
                    (key === "ProfessionalPhoto" && value) ? (
                      <img
                        src={String(value)}
                        width={IMG_SIZE}
                        height={IMG_SIZE}
                        alt={getImageAlt(key)}
                        className="img-fluid"
                        loading="lazy"
                        decoding="async"
                      />
                    ) : key === "isAnnualMembership" ? (
                      value === 0 ? "Monthly" : "Annual"
                    ) : (
                      <p className="form-control-plaintext">
                        {key === "dateOfBirth"
                          ? new Date(value as string | number).toLocaleDateString()
                          : value?.toString() || "N/A"}
                      </p>
                    )}
                  </div>
                </div>
              ))
          )}
        </div>
      </div>
    </div>
  );
}

UserProfileInner.displayName = "UserProfile";

export default memo(UserProfileInner);
